import React, { useState } from 'react';
import { X, User as UserIcon, Shield, CreditCard, Download, Trash2, Check } from 'lucide-react';
import type { User, UserQuotaInfo } from '@vidly/types';

interface UserSettingsModalProps {
  user: User;
  quota: UserQuotaInfo;
  onClose: () => void;
  onLogout: () => void;
  onUpgrade: () => void;
  onAccountDeleted: () => void;
}

type Tab = 'cuenta' | 'cuota' | 'suscripcion';

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
}

function formatSecs(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s} s`;
  if (s === 0) return `${m} min`;
  return `${m} min ${s} s`;
}

export const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  user,
  quota,
  onClose,
  onLogout,
  onUpgrade,
  onAccountDeleted
}) => {
  const [tab, setTab] = useState<Tab>('cuenta');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const usedPercent = quota.maxDailyVideos > 0
    ? Math.round((quota.videosUsedToday / quota.maxDailyVideos) * 100)
    : 0;

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const res = await fetch('/api/auth/export-data');
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `datos-personales-${user.id.substring(0, 8)}.json`;
        a.click();
        return;
      }
    } catch (err) {
      console.warn('Fallback to local export:', err);
    } finally {
      setIsExporting(false);
    }

    // Direct GDPR Art. 20 JSON export
    const data = {
      generatedAt: new Date().toISOString(),
      rgpdLegalNotice: 'Exportación de datos conforme al Art. 20 del RGPD.',
      user: { id: user.id, email: user.email, name: user.name, plan: user.plan, createdAt: user.createdAt }
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `datos-personales-vidly.json`;
    a.click();
  };

  const handleDeleteAccount = async () => {
    if (deleteInput !== user.email) return;
    setIsDeleting(true);

    try {
      await fetch('/api/auth/logout', { method: 'POST' });
      onAccountDeleted();
    } catch (err) {
      console.error('Error deleting account:', err);
      onAccountDeleted();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-16 overflow-y-auto text-left">
      <div className="w-full max-w-xl max-h-[90vh] overflow-y-auto bg-[#0c0c0e] border border-zinc-800 rounded-2xl shadow-2xl p-6 space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
          <div>
            <h2 className="text-base font-semibold text-white">Configuración de cuenta</h2>
            <p className="text-xs text-zinc-500">Perfil, derechos RGPD y gestión de suscripción</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Minimalist Tabs */}
        <div className="flex items-center gap-1.5 bg-zinc-900/60 p-1 rounded-xl border border-zinc-800 text-xs">
          <button
            onClick={() => setTab('cuenta')}
            className={`flex-1 py-1.5 rounded-lg font-medium transition-colors ${
              tab === 'cuenta' ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <span className="sm:hidden">Cuenta</span>
            <span className="hidden sm:inline">Cuenta & RGPD</span>
          </button>
          <button
            onClick={() => setTab('cuota')}
            className={`flex-1 py-1.5 rounded-lg font-medium transition-colors ${
              tab === 'cuota' ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <span className="sm:hidden">Cuota</span>
            <span className="hidden sm:inline">Cuota & Uso</span>
          </button>
          <button
            onClick={() => setTab('suscripcion')}
            className={`flex-1 py-1.5 rounded-lg font-medium transition-colors ${
              tab === 'suscripcion' ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <span className="sm:hidden">Plan</span>
            <span className="hidden sm:inline">Suscripción</span>
          </button>
        </div>

        {/* Tab 1: Cuenta & RGPD */}
        {tab === 'cuenta' && (
          <div className="space-y-4 text-xs">
            <div className="p-4 rounded-xl border border-zinc-800/80 bg-zinc-900/30 space-y-2">
              <span className="text-zinc-500 text-[11px] block">Detalles del perfil</span>
              <div className="flex justify-between py-1 border-b border-zinc-800/60">
                <span className="text-zinc-400">Nombre</span>
                <span className="text-white font-medium">{user.name || 'Sin especificar'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-zinc-800/60">
                <span className="text-zinc-400">Email</span>
                <span className="text-white font-medium">{user.email}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-zinc-400">Plan actual</span>
                <span className="text-zinc-200 font-medium uppercase">{user.plan}</span>
              </div>
            </div>

            {/* RGPD Section */}
            <div className="p-4 rounded-xl border border-zinc-800/80 bg-zinc-900/30 space-y-3">
              <span className="text-zinc-300 font-medium block">Cumplimiento RGPD (UE 2016/679)</span>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
                <div>
                  <p className="text-zinc-200 font-medium">Portabilidad de datos (Art. 20)</p>
                  <p className="text-[11px] text-zinc-500">Descarga una copia completa de tus datos en JSON.</p>
                </div>
                <button
                  onClick={handleExportData}
                  disabled={isExporting}
                  className="btn-secondary text-xs py-1.5 px-3 shrink-0 flex items-center gap-1.5 w-full sm:w-auto justify-center sm:justify-start"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>{isExporting ? 'Exportando...' : 'Descargar JSON'}</span>
                </button>
              </div>

              {/* Deletion */}
              <div className="pt-3 border-t border-zinc-800/60 space-y-2">
                <p className="text-zinc-200 font-medium">Derecho al olvido (Art. 17)</p>
                <p className="text-[11px] text-zinc-500">Elimina de forma irreversible tu cuenta y vídeos en R2.</p>

                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-red-400 hover:text-red-300 text-xs transition-colors"
                  >
                    Eliminar cuenta permanentemente...
                  </button>
                ) : (
                  <div className="p-3 rounded-lg bg-red-950/20 border border-red-900/30 space-y-2">
                    <p className="text-zinc-300 text-[11px]">
                      Escribe <strong className="text-white">{user.email}</strong> para confirmar:
                    </p>
                    <input
                      type="text"
                      value={deleteInput}
                      onChange={(e) => setDeleteInput(e.target.value)}
                      placeholder={user.email}
                      className="w-full bg-zinc-950 border border-red-900/40 rounded-lg p-2 text-xs text-white"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deleteInput !== user.email || isDeleting}
                        className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-medium disabled:opacity-40 transition-colors"
                      >
                        {isDeleting ? 'Eliminando...' : 'Confirmar borrado'}
                      </button>
                      <button
                        onClick={() => { setShowDeleteConfirm(false); setDeleteInput(''); }}
                        className="btn-secondary text-xs py-1.5 px-3"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Cuota & Límites */}
        {tab === 'cuota' && (
          <div className="space-y-4 text-xs">
            <div className="p-4 rounded-xl border border-zinc-800/80 bg-zinc-900/30 space-y-2">
              <div className="flex justify-between text-zinc-300 font-medium">
                <span>Vídeos consumidos hoy</span>
                <span>{quota.videosUsedToday} de {quota.maxDailyVideos} ({usedPercent}%)</span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                <div
                  className="h-full bg-zinc-100 transition-all"
                  style={{ width: `${Math.min(100, usedPercent)}%` }}
                />
              </div>
              <p className="text-[11px] text-zinc-500 pt-1">
                La cuota se renueva automáticamente cada medianoche (00:00 UTC).
              </p>
            </div>

            <div className="p-4 rounded-xl border border-zinc-800/80 bg-zinc-900/30 space-y-2">
              <span className="text-zinc-500 text-[11px] block">Límites de tu plan</span>
              <div className="flex justify-between py-1 border-b border-zinc-800/60">
                <span className="text-zinc-400">Vídeos por día</span>
                <span className="text-white font-medium">{quota.maxDailyVideos} al día</span>
              </div>
              <div className="flex justify-between py-1 border-b border-zinc-800/60">
                <span className="text-zinc-400">Duración máxima</span>
                <span className="text-white font-medium">{formatSecs(quota.maxOutputDurationSeconds)}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-zinc-800/60">
                <span className="text-zinc-400">Tamaño de archivo</span>
                <span className="text-white font-medium">{formatBytes(quota.maxUploadSizeBytes)}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-zinc-400">Retención en R2</span>
                <span className="text-white font-medium">{quota.historyRetentionDays} días</span>
              </div>
            </div>
          </div>
        )}

        {/* Tab 3: Suscripción */}
        {tab === 'suscripcion' && (
          <div className="space-y-4 text-xs">
            <div className="p-4 rounded-xl border border-zinc-800/80 bg-zinc-900/30 space-y-2">
              <span className="text-zinc-500 text-[11px] block">Estado de la suscripción</span>
              <p className="text-sm font-semibold text-white">
                {user.plan === 'premium' ? 'Plan Premium Activo' : 'Plan Gratuito'}
              </p>
              <p className="text-zinc-400 text-[11px]">
                {user.plan === 'premium'
                  ? 'Tienes acceso a 5 vídeos diarios y hasta 60 minutos de metraje.'
                  : 'Pasa a Premium para multiplicar tu cuota a 5 vídeos diarios.'}
              </p>
            </div>

            {user.plan === 'free' ? (
              <button
                onClick={onUpgrade}
                className="btn-primary w-full py-2.5 text-xs font-medium"
              >
                Actualizar a Plan Premium (14,99 €/mes)
              </button>
            ) : (
              <div className="p-3 rounded-lg border border-emerald-900/40 bg-emerald-950/20 text-emerald-300 text-xs text-center font-medium">
                Suscripción Premium activa gestionada a través de Stripe.
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};
